// src/app/telemetry.service.ts
import { Injectable, Injector } from '@angular/core';
import { TelemetryConfigService } from './telemetry-config.service';
import { ApplicationInsightsService } from './application-insights.service';

declare global { interface Window { cwr?: (op: string, payload?: any) => void; } }

@Injectable({ providedIn: 'root' })
export class TelemetryService {
    constructor(
        private cfg: TelemetryConfigService,
        private injector: Injector
    ) { }


    private provider(): 'aws' | 'azure' | 'none' {
        // if config isn’t ready yet, treat as 'none'
        return this.cfg.config?.provider ?? 'none';
    }

    setUser(userId: string) {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).setUser(userId);
        } else if (p === 'aws') {
            // Prefer NOT sending PII: hash or use an internal pseudonymous ID
            const safeId = userId; // optionally replace with await this.hash(userId)
            window.cwr?.('addSessionAttributes', { userId: safeId }); // RUM session attribute
        }
    }

    clearUser() {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).clearUser();
        } else if (p === 'aws') {
            // Overwrite with empty string for the rest of the session
            window.cwr?.('addSessionAttributes', { userId: '' });
        }
    }

    setContext(opts: { unitId?: string }) {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).setContext(opts);
        } else if (p === 'aws') {
            if (typeof opts.unitId !== 'undefined') {
                window.cwr?.('addSessionAttributes', { unitId: String(opts.unitId) });
            }
        }
    }

    trackEvent(name: string, props?: Record<string, any>) {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).logEvent(name, props);
        } else if (p === 'aws') {
            window.cwr?.('recordEvent', { type: name, ...props });
        }
    }

    trackPageView(name?: string) {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).logPageView(name);
        } else if (p === 'aws') {
            window.cwr?.('recordPageView', name ?? window.location.pathname);
        }
    }

    trackException(error: Error, props?: Record<string, any>) {
        const p = this.cfg.config.provider;
        if (p === 'azure') {
            this.injector.get(ApplicationInsightsService).logException(error);
        } else if (p === 'aws') {
            window.cwr?.('recordError', error);
        }
    }
}
